/**
 * Reader for the platform notification `elementPath` payload, in both formats the backend emits.
 *
 * v1 keys an entry by bare type (`"SECTION"`), so a type appears at most once. v2 qualifies the key
 * with the level (`"SECTION@5"`) and repeats the type in the value, because the drive hierarchy nests
 * arbitrarily — `SECTION > SUB-SECTION > SECTION` is a real shape. One payload can mix both.
 *
 * Mirrors the backend's ElementPathService (and @mtm/task-ui-angular's element-path.utils) so client
 * and server resolve the same ancestor: for a repeated type the DEEPEST occurrence wins, and the level
 * is read from the value's `lv` only, never derived from the `@N` key suffix.
 */

export interface ElementPathNode {
  type?: string;
  id?: string;
  lv?: number | string;
  name?: string;
}

export type RawElementPath = string | Record<string, ElementPathNode> | null | undefined;

interface RankedNode {
  node: ElementPathNode;
  hasId: boolean;
  level: number | undefined;
}

/** `SUB_SECTION` (entity APIs) and `SUB-SECTION` (path keys) name the same type. */
function normalizeType(type: string): string {
  return type.trim().toUpperCase().replace(/_/g, '-');
}

function resolveType(key: string, node: ElementPathNode): string {
  if (typeof node.type === 'string' && node.type.trim()) {
    return normalizeType(node.type);
  }
  const separator: number = key.indexOf('@');
  return normalizeType(separator === -1 ? key : key.slice(0, separator));
}

// The backend reads a numeric lv through intValue() (truncates) but parses a string lv with
// Integer.valueOf, which throws on "5.5" and leaves the entry unrankable. Same split here.
function parseLevel(lv: number | string | undefined): number | undefined {
  if (typeof lv === 'number') {
    return Number.isFinite(lv) ? Math.trunc(lv) : undefined;
  }
  if (typeof lv === 'string') {
    const trimmed: string = lv.trim();
    return /^[+-]?\d+$/.test(trimmed) ? Number(trimmed) : undefined;
  }
  return undefined;
}

function stringOrUndefined(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw ? raw : undefined;
}

// Deepest-with-an-id wins, matching the server's group-by; an entry with no parseable lv is
// unrankable and loses to any ranked sibling of its type; ties fall through to insertion order.
function beats(candidate: RankedNode, incumbent: RankedNode): boolean {
  if (candidate.hasId !== incumbent.hasId) {
    return candidate.hasId;
  }
  if (candidate.level !== undefined && incumbent.level !== undefined) {
    return candidate.level >= incumbent.level;
  }
  if (incumbent.level !== undefined) {
    return false;
  }
  return true;
}

/**
 * Parses an `elementPath` payload (JSON string or object, v1/v2/mixed keying) into a map keyed by
 * normalized bare type, one winning occurrence per type. Never throws; garbage input yields `{}`.
 */
export function normalizeElementPath(raw: RawElementPath): Record<string, ElementPathNode> {
  let parsed: unknown = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  const winners: Record<string, RankedNode> = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    const node: ElementPathNode = value as ElementPathNode;
    const type: string = resolveType(key, node);
    if (!type) {
      continue;
    }
    // Sanitize id/name like the reference's stringOrUndefined: a non-string id must not
    // out-rank a string-id sibling, or this map crowns a different ancestor than the server.
    const id: string | undefined = stringOrUndefined(node.id);
    const candidate: RankedNode = {
      node: { type, id, lv: node.lv, name: stringOrUndefined(node.name) },
      hasId: !!id,
      level: parseLevel(node.lv),
    };
    if (!winners[type] || beats(candidate, winners[type])) {
      winners[type] = candidate;
    }
  }

  const path: Record<string, ElementPathNode> = {};
  for (const [type, winner] of Object.entries(winners)) {
    path[type] = winner.node;
  }
  return path;
}
